import {
	CheckCircleFilled,
	CloseCircleOutlined,
	CloseOutlined,
	DeleteOutlined,
	ExclamationCircleOutlined,
	LeftOutlined,
	PlusOutlined
} from '@ant-design/icons';
import {
	Button,
	Col,
	Form,
	Input,
	message,
	Modal,
	Radio,
	RadioChangeEvent,
	Row,
	Select,
	Space,
	Spin,
	Typography
} from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import TitleBox from 'src/components/title-box/title-box';
import { GuaranteeTypeEnum } from 'src/constants/guarantee';

import {yesNoOptions } from 'src/constants/mortgage-deal';
import useStore from 'src/hooks/useStore';
import { IGuarantor } from 'src/interfaces/mortgage-deal/invite-guarantor';

import { i18nKey } from 'src/locales/i18n';
import { IMortgageDealDetails } from 'src/store/mortgage-deal/mortgage-deal-details.store';
import { IUserStore } from 'src/store/user';

import styles from './invite-guarantor.module.less';
import UploadFileGuarantor from './upload-file-guarantor/upload-file-guarantor';

interface IProps {
	onBack?: () => void;
	onConfirmForm?: () => void;
	isCorporate?: boolean;
	inviteForm: FormInstance;
	isViewFullForm?: boolean;
}

const InviteGuarantor = ({ onBack, onConfirmForm, isCorporate = false, inviteForm, isViewFullForm }: IProps) => {
	const [loading, setLoading] = useState(false);
	const [t] = useTranslation();

	const [isInvite, setIsInvite] = useState(isCorporate);

	const [isOpenResendModal, setOpenResendModal] = useState(false);

	const [guarantor, setGuarantor] = useState<IGuarantor | undefined>();

	const addInviteBtn = useRef<HTMLDivElement>(null);

	const mortgageDealDetails: IMortgageDealDetails = useStore('mortgageDealDetails');

	const [searchParams] = useSearchParams();

	const toInviteGuarantor = searchParams.get('toInviteGuarantor');

	const userStore: IUserStore = useStore('userStore');

	const mortgageId = searchParams.get('id');

	const relationshipOption = [
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.parents),
			value: '0'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Spouse),
			value: '1'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Couple),
			value: '2'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Grandparents),
			value: '3'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Sisters),
			value: '4'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Brothers),
			value: '5'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Fiance),
			value: '6'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Fiancee),
			value: '7'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Friends),
			value: '8'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.BusinessPartners),
			value: '9'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.Others),
			value: '10'
		}
	];

	const onChangeInvite = (e: RadioChangeEvent) => {
		if (e.target.value) {
			inviteForm.setFieldValue(['guarantorList'], mortgageDealDetails.guarantors);

			setIsInvite(true);

			setTimeout(() => {
				addInviteBtn.current && addInviteBtn.current.click();
			}, 250);
		} else {
			inviteForm.setFieldValue(['guarantorList'], []);

			setIsInvite(false);
		}
	};

	const onSaveClick = () => {
		inviteForm.submit();
	};

	const onFileUpload = async (file: File, idx: number) => {
		setLoading(true);
		const form = new FormData();

		form.append('file', file);

		form.append('fileName', file.name);

		const fileName = await mortgageDealDetails.uploadFileMainGuarantor(form, idx);

		if (fileName) {
			message.success('Uploaded File.');
			setLoading(false);
		} else {
			message.error('Uploaded failed');
			setLoading(false);
		}
	};

	const onFinish = async () => {
		setLoading(true);
		const listGuarantor: IGuarantor[] = inviteForm.getFieldValue(['guarantorList']);
		let rs = false;

		if ((await inviteForm.getFieldValue('inviteAGuarantor')) === true) {
			rs = await mortgageDealDetails.saveGuarantor(listGuarantor, true);
		} else {
			rs = await mortgageDealDetails.saveGuarantor(listGuarantor, false);
		}

		if (rs) {
			onConfirmForm && onConfirmForm();
		}
		setLoading(false);
	};

	const onOpenResendModal = async (index: number) => {
		const value = inviteForm.getFieldValue(['guarantorList', index]);

		if (value.email) {
			setOpenResendModal(true);
			setGuarantor(value);
		}
	};

	const onCancel = () => {
		setOpenResendModal(false);
	};

	const onResendEmailGuarantor = async () => {
		setLoading(true);
		const rs = await mortgageDealDetails.resendEmailGuarantor(guarantor || {});

		if (rs) {
			message.success(`${t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.resendMessage)}`);
			setGuarantor(undefined);
			mortgageDealDetails
				.getMortgageDealDetails(mortgageId || '')
				.then(() => {
					inviteForm.setFieldsValue({
						guarantorList: mortgageDealDetails.guarantors
					});
				})
				.catch();
		}
		setLoading(false);
		setOpenResendModal(false);
	};

	const disabledOptionNo = () => {
		if (mortgageDealDetails.guarantors.length > 0) {
			return true;
		}
		if (isCorporate) {
			return true;
		}
		if (mortgageDealDetails.memberHasConsentedInGuarantorList) {
			return true;
		}

		return false;
	};

	const disableWhenEmailHasSended = (index: number): boolean => {
		if (!mortgageDealDetails.guarantors[index]) {
			return false;
		}
		switch (mortgageDealDetails.guarantors[index].status) {
			case GuaranteeTypeEnum.PendingToConsent:
			case GuaranteeTypeEnum.Declined:
			case GuaranteeTypeEnum.Consented:
				return true;

			default:
				return false;
		}
	};

	const renderButtonDelete = (index: number, remove: (index: number) => void) => {
		switch (mortgageDealDetails.guarantors[index] && mortgageDealDetails.guarantors[index].status) {
			case GuaranteeTypeEnum.Consented:
				return (
					<>
						<CheckCircleFilled
							style={{
								fontSize: '24px',
								color: '#5DA84F'
							}}
						/>
					</>
				);

			case GuaranteeTypeEnum.Declined:
				return (
					<Row gutter={10}>
						<Col span={12}>
							<CloseCircleOutlined
								style={{
									fontSize: '24px',
									color: 'white',
									backgroundColor: '#FF5059',
									borderRadius: '100%',
									border: 'none'
								}}
							/>
						</Col>
						<Col span={12}>
							<DeleteOutlined
								style={{
									fontSize: '24px',
									cursor: 'pointer'
								}}
								onClick={async () => {
									const rs: boolean = await mortgageDealDetails.removeGuarantorInvitation(index);

									if (rs) {
										remove(index);
									}
								}}
							/>
						</Col>
					</Row>
				);

			default:
				return (
					<>
						<DeleteOutlined
							style={{
								fontSize: '24px',
								cursor: 'pointer'
							}}
							onClick={async () => {
								const rs: boolean = await mortgageDealDetails.removeGuarantorInvitation(index);

								if (rs) {
									remove(index);
								}
							}}
						/>
					</>
				);
		}
	};

	const disableButtonResend = () => {
		if (guarantor && !guarantor.canResendEmail) {
			return true;
		}

		return false;
	};

	const onFormFailed = () => {
		const firstInvalidFormItem = inviteForm.getFieldsError()[0];
		if (firstInvalidFormItem) {
			inviteForm.scrollToField(firstInvalidFormItem.name[0], {
				behavior: 'smooth'
			});
		}
	};

	const renderEmailGuarantor = (index: number) => {
		if (
			mortgageDealDetails.guarantors[index] &&
			mortgageDealDetails.guarantors[index].status === GuaranteeTypeEnum.PendingToConsent
		) {
			return (
				<>
					<Col xs={20} sm={20} md={20} lg={20} xl={18}>
						<Form.Item
							name={[index, 'email']}
							required={false}
							rules={[
								{
									required: isInvite,
									message: `${t(i18nKey.onboarding.validation.required)}`
								},
								{
									type: 'email',
									message: `${t(i18nKey.registration.validation.wrongEmailFormat)}`
								},
								() => ({
									validator(_, value) {
										if (value === userStore.email) {
											return Promise.reject(
												new Error(`${t(i18nKey.onboarding.validation.accountDetail.required.hkIDNoFormat)}`)
											);
										}
										return Promise.resolve();
									}
								})
							]}>
							<Input disabled={disableWhenEmailHasSended(index)} />
						</Form.Item>
					</Col>
					<Col xs={4} sm={4} md={4} lg={4} xl={6}>
						<a style={{ textDecoration: 'underline' }} onClick={() => onOpenResendModal(index)}>
							{t(i18nKey.mortgageDeal.resend)}
						</a>
					</Col>
				</>
			);
		}

		return (
			<>
				<Col span={24}>
					<Form.Item
						name={[index, 'email']}
						required={false}
						rules={[
							{
								required: isInvite,
								message: `${t(i18nKey.onboarding.validation.required)}`
							},
							{
								type: 'email',
								message: `${t(i18nKey.registration.validation.wrongEmailFormat)}`
							},
							() => ({
								validator(_, value) {
									if (value === userStore.email) {
										return Promise.reject(
											new Error(`${t(i18nKey.mortgageDeal.requestForm.validate.borrowerCanNotBecomeAGuarantor)}`)
										);
									}
									return Promise.resolve();
								}
							})
						]}>
						<Input disabled={disableWhenEmailHasSended(index)} />
					</Form.Item>
				</Col>
			</>
		);
	};

	useEffect(() => {
		if (mortgageId) {
			setLoading(true);
			inviteForm.setFieldsValue({
				guarantorList: mortgageDealDetails.guarantors
			});
			if (mortgageDealDetails.mainGuarantor) {
				inviteForm.setFieldValue('inviteAGuarantor', true);
				setIsInvite(true);
			}

			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!isCorporate) {
			if (mortgageDealDetails.memberHasConsentedInGuarantorList || mortgageDealDetails.guarantors.length > 0) {
				inviteForm.setFieldValue('inviteAGuarantor', true);
				setIsInvite(true);
			} else {
				if (!mortgageId) {
					inviteForm.setFieldValue('inviteAGuarantor', undefined);
					return;
				}
				inviteForm.setFieldValue('inviteAGuarantor', false);
				setIsInvite(false);
			}
		} else {
			if (mortgageId && mortgageDealDetails.guarantors.length === 0) {
				inviteForm.setFieldValue('inviteAGuarantor', true);
			} else {
				addInviteBtn.current && addInviteBtn.current.click();
				inviteForm.setFieldValue('inviteAGuarantor', true);
			}
		}
	}, []);

	return (
		<>
			<Spin spinning={loading}>
				<Form form={inviteForm} onFinish={onFinish} disabled={isViewFullForm} onFinishFailed={onFormFailed}>
					<TitleBox>
						<span>{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.title)}</span>
					</TitleBox>
					<div className={styles.subForm}>
						<Row>
							<Col xs={24} sm={24} md={24} lg={24}>
								<span className={styles.titleQuestion}>
									{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.title)}?
								</span>
								<span className={styles.requireAsterisk}>*</span>
							</Col>
							<Col xs={24} sm={24} md={24} lg={24}>
								<Form.Item
									name="inviteAGuarantor"
									required={false}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Radio.Group style={{ width: '100%' }} onChange={onChangeInvite}>
										<Row style={{ width: '100%' }}>
											<Col xs={12} sm={12} md={12} lg={4}>
												<Radio value={yesNoOptions[0].value}>{t(i18nKey.common.yes)}</Radio>
											</Col>
											<Col xs={12} sm={12} md={12} lg={4}>
												<Radio value={yesNoOptions[1].value} disabled={disabledOptionNo()}>
													{t(i18nKey.common.no)}
												</Radio>
											</Col>
										</Row>
									</Radio.Group>
								</Form.Item>
							</Col>
						</Row>
					</div>
					{isInvite && (
						<>
							<Row>
								<div className={styles.box}>
									<Row>
										<Col xs={21} sm={21} md={23}>
											<Typography.Title level={5}>
												{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.guarantor)}
											</Typography.Title>
										</Col>
										<Col xs={3} sm={3} md={1}>
											<Button
												type="primary"
												icon={<PlusOutlined />}
												onClick={() => {
													if (addInviteBtn && addInviteBtn.current) {
														addInviteBtn.current.click();
													}
												}}></Button>
										</Col>
									</Row>

									<Form.List name="guarantorList">
										{(fields, { add, remove }) => (
											<>
												<span
													ref={addInviteBtn}
													onClick={() => {
														if (fields.length < 4) {
															add();
														}
													}}></span>
												{fields.map((f, idx) => (
													<Row key={f.key} style={{ marginBottom: 30 }}>
														<Col xs={24} sm={24} md={24} lg={5}>
															<Row>
																<span className={styles.titleForm}>
																	{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.name)}
																	<span className={styles.requireAsterisk}>*</span>
																</span>
															</Row>
															<Row className={styles.formBox}>
																<Form.Item
																	name={[idx, 'name']}
																	required={false}
																	rules={[
																		{
																			required: isInvite,
																			message: `${t(i18nKey.onboarding.validation.required)}`
																		}
																	]}
																	style={{ width: '100%' }}>
																	<Input disabled={disableWhenEmailHasSended(idx)} />
																</Form.Item>
															</Row>
														</Col>
														<Col xs={24} sm={24} md={24} lg={6}>
															<Row>
																<span className={styles.titleForm}>
																	{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.relationshipWithBorrower)}
																	<span className={styles.requireAsterisk}>*</span>
																</span>
																
															</Row>
															<Row className={styles.formBox}>
																<Form.Item
																	name={[idx, 'relationship']}
																	style={{ width: '100%' }}
																	required={false}
																	rules={[
																		{
																			required: isInvite,
																			message: `${t(i18nKey.onboarding.validation.required)}`
																		}
																	]}>
																	<Select
																		options={relationshipOption}
																		placeholder={`${t(i18nKey.common.select)}`}
																		disabled={disableWhenEmailHasSended(idx)}
																	/>
																</Form.Item>
															</Row>
														</Col>
														<Col xs={24} sm={24} md={24} lg={6}>
															<Row>
																<span className={styles.titleForm}>
																	{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.emailAddress)}
																	<span className={styles.requireAsterisk}>*</span>
																</span>
																
															</Row>
															<Row className={styles.formBox} gutter={5}>
																{renderEmailGuarantor(idx)}
															</Row>
														</Col>
														<Col xs={24} sm={24} md={24} lg={7}>
															<Row>
																<Row>
																	<span className={styles.titleForm}>
																		{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.proofOfRelationship)}
																		<span className={styles.requireAsterisk}>*</span>
																	</span>
																</Row>
																<Row className={styles.formBox} style={{ width: '100%' }}>
																	<Col span={20}>
																		<Form.Item
																			name={[idx, 'documentId']}
																			style={{ width: '100%' }}
																			required={false}
																			rules={[
																				{
																					required: isInvite,
																					message: `${t(i18nKey.onboarding.validation.required)}`
																				}
																			]}>
																			<UploadFileGuarantor
																				disableUploadButton={disableWhenEmailHasSended(idx)}
																				inputFile={
																					mortgageDealDetails.guarantors && !!mortgageDealDetails.guarantors[idx]
																						? mortgageDealDetails.guarantors[idx].document
																						: {}
																				}
																				onChange={(file) => onFileUpload(file, idx)}
																			/>
																		</Form.Item>
																	</Col>
																	<Col xs={4} sm={4} md={4} lg={4}>
																		{renderButtonDelete(idx, remove)}
																	</Col>
																</Row>
															</Row>
														</Col>
													</Row>
												))}
											</>
										)}
									</Form.List>
								</div>
							</Row>
						</>
					)}
				</Form>
				{!isViewFullForm && (
					<Row
						gutter={[
							{ xs: 0, sm: 0, md: 0, lg: 132 },
							{ xs: 20, sm: 20, md: 20, lg: 16 }
						]}
						style={{ marginTop: 30 }}>
						<Col span={24}>
							<Row gutter={16}>
								<Col xs={5} sm={5} md={5} lg={2}>
									<Button block type="primary" onClick={onBack} disabled={toInviteGuarantor ? true : false}>
										<LeftOutlined />
									</Button>
								</Col>
								<Col xs={19} sm={19} md={19} lg={22}>
									<Button block type="primary" onClick={onSaveClick}>
										{t(i18nKey.onboarding.saveAndNext)}
									</Button>
								</Col>
							</Row>
						</Col>
					</Row>
				)}
			</Spin>
			<Modal
				open={isOpenResendModal}
				destroyOnClose
				title={null}
				footer={null}
				width={726}
				onCancel={onCancel}
				style={{
					borderRadius: 11
				}}
				centered
				closeIcon={
					<CloseOutlined
						style={{
							fontSize: '21px',
							color: '#595959'
						}}
					/>
				}>
				<Space align="center">
					<ExclamationCircleOutlined style={{ fontSize: '24px', color: '#FAAD14' }} />
					<div className={styles.confirmContent}>
						<p className={styles.confirmTitle}>{t(i18nKey.mortgageDeal.resendInvitationEmail)}</p>
					</div>
				</Space>
				<Row style={{ margin: '24px 0 30px 0' }}>
					<Col span={24}>{t(i18nKey.mortgageDeal.ResendInvitationEmailToGuarantor)}</Col>
				</Row>
				<Row gutter={20} style={{ marginBottom: 20 }}>
					<Col span={12}>
						<Button
							style={{ width: '100%', backgroundColor: '#848484', color: '#fff', maxHeight: '57px' }}
							onClick={() => onCancel()}>
							{t(i18nKey.common.cancel)}
						</Button>
					</Col>
					<Col span={12} className={disableButtonResend() ? styles.buttonDisable : styles.buttonConfirm}>
						<Button onClick={onResendEmailGuarantor} disabled={disableButtonResend()}>
							{t(i18nKey.mortgageDeal.resend)}
						</Button>
					</Col>
				</Row>
				{disableButtonResend() && (
					<>
						<Row gutter={20}>
							<Col span={24}>{t(i18nKey.mortgageDeal.requestForm.inviteGuarantor.resendTomorrow)}</Col>
						</Row>
					</>
				)}
			</Modal>
		</>
	);
};

export default observer(InviteGuarantor);
